import { z } from "zod";

/**
 * Two application roles in Version 1.0. A read-only Executive Viewer is anticipated
 * for a later phase — no Version 1.0 behaviour may assume it exists.
 */
export const ROLES = {
  salesManager: "sales_manager",
  headOfSales: "head_of_sales",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const roleSchema = z.enum([ROLES.salesManager, ROLES.headOfSales]);

/**
 * Role names for the **audit trail**, not for the interface.
 *
 * These are what the API stamps into an audit row's `old_value`/`new_value` when a role
 * changes: display values, written once at write time, in one language, so that a reader in
 * either language sees the same history rather than one rewritten under them.
 *
 * The interface reads `role.*` from the web catalogue (`apps/web/src/i18n/`) instead — do not
 * import these into a component. See `specs/i18n.md`.
 */
export const ROLE_LABELS: Record<Role, string> = {
  sales_manager: "Sales Manager",
  head_of_sales: "Head of Sales",
};

/**
 * Derived user state. Never set directly — it follows from activation and the active
 * flag. An invited user who has never signed in is neither active nor deactivated,
 * and an administrator needs to see the difference.
 */
export const USER_STATES = {
  invited: "invited",
  active: "active",
  deactivated: "deactivated",
} as const;

export type UserState = (typeof USER_STATES)[keyof typeof USER_STATES];

export const userStateSchema = z.enum([USER_STATES.invited, USER_STATES.active, USER_STATES.deactivated]);

/** As `ROLE_LABELS`: audit-row display values, not interface copy. */
export const USER_STATE_LABELS: Record<UserState, string> = {
  invited: "Invited",
  active: "Active",
  deactivated: "Deactivated",
};

export function deriveUserState(user: { active: boolean; activatedAt: Date | string | null }): UserState {
  if (!user.active) return USER_STATES.deactivated;
  return user.activatedAt ? USER_STATES.active : USER_STATES.invited;
}
